/**
 * What a screen reader is told when one pass lands.
 *
 * A pass runs for most of a minute, and nothing on the page said when it had
 * finished: the canvas grows, the thread gains a message, and a reader who
 * cannot see either waited in silence. This is the sentence the page's polite
 * announcer speaks (`ui/Announcer.tsx`) — one per pass, from the STORED RECORD
 * and nothing else: the op's own row (its verb, its targets, what it produced,
 * whether it failed or was declined) and the record a check writes (its reading
 * and its kept sources' stances). Never the model's prose — no summary, no
 * thinking, no narration — because a sentence read aloud with no way to see
 * where it came from has to be one the record stands behind.
 *
 * The words are the record's own words: `opLabel` for the verb and
 * `READING_LABEL` for a check, as the card and the feed print them — and for a
 * pass whose product is a record of its own, that record's words: a prior-art
 * search's reading label, a sweep's sentence, a conjecture pass's count (D289).
 */

import { offTopicSupport } from './judged'
import { READING_LABEL, stanceTally, type Op, type Session, type Verification } from './types'
import {
  DESIGN_UNLABELLED,
  EXECUTABLE_NOT_RUN,
  NO_TEST_ADDED,
  STATUS_WORD,
  conjecturesProposed,
  landedNoun,
} from './vocabulary'
import { executableOutcome, isExecutableTest, opLabel } from './narrationReceipt'
import { laterStatements, priorArtLabel } from './priorArt'
import { sweepSentence } from './sweep'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** “Label”, “Label” and “Label” — the op's targets by name, as the card names them. */
function targetNames(session: Session, op: Op): string {
  const names = op.targetIds
    .map((id) => session.nodes[id]?.label)
    .filter((l): l is string => !!l)
    .map((l) => `“${l}”`)
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The check this op wrote: the live record if it is this op's, else the history. */
function checkOf(session: Session, op: Op): Verification | null {
  for (const id of op.targetIds) {
    const n = session.nodes[id]
    if (!n) continue
    if (n.verification?.opId === op.id) return n.verification
    const old = n.verificationHistory?.find((v) => v.opId === op.id)
    if (old) return old
  }
  return null
}

/**
 * The kept sources' sides, counted — "3 sources dispute this, none support it".
 * The stances are the check's labels on sources the citation gate admitted, so
 * this counts what the record says and rules on nothing. Empty when no source was
 * kept.
 *
 * The strip the card and the feed print, said as a sentence: the same count
 * (`stanceTally`), because a line a screen reader hears and a line a reader sees
 * about one check are one count or two that can disagree. A sentence rather than
 * the strip itself, since "3 dispute · 0 support" read aloud is three fragments.
 * Sources the check gave no side are said as such, and never as taking none.
 */
export function stanceCount(v: Verification): string {
  const { disputes, supports, unlabelled, kept } = stanceTally(v.sources)
  const blank = unlabelled ? `, ${unlabelled} ${DESIGN_UNLABELLED}` : ''
  if (disputes === 0 && supports === 0) {
    if (!kept) return ''
    return unlabelled === kept
      ? `${plural(kept, 'source')} kept, ${unlabelled === 1 ? 'its side' : 'their sides'} ${DESIGN_UNLABELLED}`
      : `${plural(kept, 'source')} kept, none taking a side${blank}`
  }
  const d = disputes
    ? `${plural(disputes, 'source')} ${disputes === 1 ? 'disputes' : 'dispute'} this`
    : ''
  const s = supports
    ? `${disputes ? supports : plural(supports, 'source')} ${supports === 1 ? 'supports' : 'support'} it`
    : ''
  // The judge's one strong call, said where the support is (D378).
  const off = offTopicSupport(v.sources)
  const judged = off
    ? `; ${off.model} read ${off.n === 1 ? 'one of the supporting quotes' : `${off.n} of the supporting quotes`} as off-topic`
    : ''
  if (d && s) return `${d}, ${s}${blank}${judged}`
  return d ? `${d}, none support it${blank}` : `${s}, none dispute it${blank}${judged}`
}

/**
 * The sentence for a pass that has just started — "Deepen started on “Label”."
 * — or null where its landing would say nothing either.
 *
 * A press gave a screen reader no word at all until the pass landed most of a
 * minute later: the press stays where it was, nothing under focus changes, and
 * the streaming message is marked busy. This is the half of `landingLine` that
 * says the press was taken. The verb and the targets are the op row's, as the
 * landing's are; a scan with no targets is named as the scan it is. An
 * executable test is named for the one conjecture it tests, where a Lab analysis
 * reads a whole packet and is named for none.
 */
export function startLine(session: Session, op: Op): string | null {
  if (op.kind === 'portfolio' || op.narration) return null
  const verb = opLabel(op, session)
  if (op.kind === 'bridge' && !op.targetIds.length) return `${verb} scan started.`
  const names =
    op.kind === 'research' && !isExecutableTest(session, op) ? '' : targetNames(session, op)
  return `${verb} started${names ? ` on ${names}` : ''}.`
}

/**
 * The sentence for one finished op, or null when there is nothing a reader
 * needs to hear (a portfolio pass is a run's, and the run's pod speaks for it).
 *
 * A narration's receipt is null too. It is filed as an op of kind `research`
 * (D91) only so the ledger and every codec stay untouched, and `OP_LABEL` read
 * off that kind announced the voice's commentary as "Research analysis
 * finished." — a pass that never ran, said aloud with nothing on screen to
 * correct it. It is not a pass anybody pressed: it takes no lock and reads the
 * graph after one. And the only true sentence about it would be its words,
 * which are the model's prose on a channel with no search — exactly what this
 * line never speaks. The narration itself is in the thread, under the pass it
 * follows.
 */
export function landingLine(session: Session, op: Op): string | null {
  if (op.kind === 'portfolio' || op.narration) return null
  const verb = opLabel(op, session)
  const names = targetNames(session, op)
  const on = names ? ` on ${names}` : ''
  // What landed BESIDE the targets. The worked example's opening counts the
  // seed it planted among its products; no pass lands the node it was run on.
  const produced = op.produced.filter((id) => !op.targetIds.includes(id)).length

  if (op.refused) return `${verb}${on} was declined by the model.`
  if (op.error) {
    return produced
      ? `${verb}${on} stopped early, after landing ${plural(produced, 'claim')}.`
      : `${verb}${on} ended without a result.`
  }

  switch (op.kind) {
    case 'verify': {
      const v = checkOf(session, op)
      if (!v) return `${verb} finished${on} with nothing to record.`
      const counts = stanceCount(v)
      return `${verb} finished${on}: ${READING_LABEL[v.reading]}${counts ? `, ${counts}` : ''}.`
    }
    case 'survey': {
      const seed = session.nodes[op.targetIds[0] ?? '']
      const survey =
        seed?.survey?.opId === op.id
          ? seed.survey
          : seed?.surveyHistory?.find((x) => x.opId === op.id)
      return survey
        ? `${verb} finished: ${plural(survey.sources.length, 'source')} kept.`
        : `${verb} finished with nothing to record.`
    }
    // Checks and scans whose product is a record rather than a node. Their own
    // endings are already said in their toasts, which carry their readings;
    // the announcer's job is only that the pass is over. A research analysis
    // reads a whole packet of claims, so it is not named claim by claim.
    case 'link':
      return `${verb} finished${on}.`
    // An executable test is not a Lab analysis, though it is filed as one (D283):
    // it tests one conjecture, and what it left is a contract in the Lab or none.
    // Never the contract's title, which is the model's words.
    case 'research': {
      const test = executableOutcome(session, op)
      if (test === null) return `${verb} finished.`
      if (test === 'none') return `${verb} finished${on}: ${NO_TEST_ADDED.toLowerCase()}.`
      const ran = test.executions
        ? `run ${test.executions === 1 ? 'once' : `${test.executions} times`}`
        : EXECUTABLE_NOT_RUN.toLowerCase()
      return `${verb} finished${on}: a test waits in Research Lab, ${ran}.`
    }
    // The three conjecture passes each write a record whose words exist, and the
    // default below would have said "landed nothing new" over all three — over a
    // prior-art search that read "Stated before", which says the one thing
    // invariant 12 forbids aloud while the toast beside it said the opposite.
    case 'priorArt': {
      const c = session.nodes[op.targetIds[0] ?? '']?.conjecture
      const search = c?.priorArt?.find((p) => p.opId === op.id)
      return c && search
        ? `${verb} finished${on}: ${priorArtLabel(search, laterStatements(c, search))}.`
        : `${verb} finished${on} with nothing to record.`
    }
    case 'sweep': {
      // The sweep's own sentence, empty case included: a sweep that kept nothing
      // is a record of that search, never a statement that the field is settled.
      const sweep = session.sweeps?.find((w) => w.opId === op.id)
      return sweep
        ? `${verb} finished${on}. ${sweepSentence(sweep)}`
        : `${verb} finished${on} with nothing to record.`
    }
    case 'conjecture':
      // D281's words, as a run's move row says them: an empty pass is an answer.
      return `${verb} finished${on}: ${produced ? conjecturesProposed(produced) : STATUS_WORD.noConjectureLanded.toLowerCase()}.`
    case 'bridge':
      // With no targets it is the scan that proposes pairings; with two it is
      // the synthesis of one pair, which lands a node.
      if (!op.targetIds.length) return `${verb} scan finished.`
      return produced
        ? `${verb} landed ${plural(produced, 'claim')} joining ${names}.`
        : `${verb} of ${names} landed nothing new.`
    // The panel lands at most one objection, its sharpest dissent; the
    // cross-examination lands what it found. Their verdicts are records on the
    // node, said in their own toasts.
    case 'panel':
      return `${verb} finished${on}${produced ? `, and landed ${plural(produced, landedNoun(op.kind))}` : ''}.`
    case 'audit':
      return `${verb} finished${on}${produced ? `, and landed ${plural(produced, 'claim')}` : ''}.`
    default:
      return produced
        ? `${verb} landed ${plural(produced, landedNoun(op.kind))}${on}.`
        : `${verb} landed nothing new${on}.`
  }
}
